import * as THREE from 'three';
import * as CANNON from 'cannon-es';

export class Tank {
  public mesh: THREE.Group;
  public body: CANNON.Body;
  public health: number = 100;
  public maxHealth: number = 100;
  public isAlive: boolean = true;
  public hasShield: boolean = false;
  public shieldTime: number = 0;

  private turretGroup!: THREE.Group;
  private barrelMesh!: THREE.Mesh;

  public turretRotation: number = 0;
  private moveSpeed: number = 10;
  private turnSpeed: number = 2;
  protected currentRotation: number = 0; // 跟踪当前的 Y 轴旋转角度

  public canShoot: boolean = true;
  private shootCooldown: number = 0.5; // 射击冷却时间
  private cooldownTimer: number = 0;

  constructor(x: number, z: number, color: number = 0x00ff00) {
    // 创建坦克网格组
    this.mesh = new THREE.Group();

    // 构建精细坦克模型
    this.buildDetailedTankModel(color);

    // 设置初始位置
    this.mesh.position.set(x, 0, z);

    // 创建物理体
    const shape = new CANNON.Box(new CANNON.Vec3(1, 0.5, 1.5));
    this.body = new CANNON.Body({
      mass: 500,
      shape: shape,
      position: new CANNON.Vec3(x, 0.5, z),
      linearDamping: 0.5,
      angularDamping: 0.5
    });

    // 添加用户数据
    this.body.userData = { type: 'tank', tank: this };
  }

  private buildDetailedTankModel(color: number): void {
    // === 履带系统 ===
    this.createTracks(color);

    // === 车身主体 ===
    this.createMainBody(color);

    // === 炮塔系统 ===
    this.createTurret(color);

    // === 车身细节 ===
    this.createBodyDetails(color);
  }

  private createTracks(color: number): void {
    const trackColor = color === 0x00ff00 ? 0x2a4a2a : 0x4a2a2a; // 深色履带
    const trackMaterial = new THREE.MeshStandardMaterial({
      color: trackColor,
      roughness: 0.9,
      metalness: 0.3
    });

    // 左履带
    const leftTrack = new THREE.Group();
    leftTrack.position.set(-1, 0.3, 0);

    // 履带板（上部）
    const trackTopGeo = new THREE.BoxGeometry(0.3, 0.15, 3.2);
    const trackTop = new THREE.Mesh(trackTopGeo, trackMaterial);
    trackTop.position.y = 0.2;
    trackTop.castShadow = true;
    leftTrack.add(trackTop);

    // 履带板（下部）
    const trackBottomGeo = new THREE.BoxGeometry(0.3, 0.1, 3.2);
    const trackBottom = new THREE.Mesh(trackBottomGeo, trackMaterial);
    trackBottom.castShadow = true;
    leftTrack.add(trackBottom);

    // 履带轮（5个）
    const wheelMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a1a1a,
      roughness: 0.7,
      metalness: 0.5
    });

    for (let i = 0; i < 5; i++) {
      const wheelGeo = new THREE.CylinderGeometry(0.25, 0.25, 0.2, 12);
      const wheel = new THREE.Mesh(wheelGeo, wheelMaterial);
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(0, 0, -1.4 + i * 0.7);
      wheel.castShadow = true;
      leftTrack.add(wheel);
    }

    this.mesh.add(leftTrack);

    // 右履带（镜像）
    const rightTrack = leftTrack.clone();
    rightTrack.position.set(1, 0.3, 0);
    this.mesh.add(rightTrack);
  }

  private createMainBody(color: number): void {
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: color,
      roughness: 0.6,
      metalness: 0.4
    });

    // 主车身（下层）
    const lowerBodyGeo = new THREE.BoxGeometry(2.2, 0.6, 3.4);
    const lowerBody = new THREE.Mesh(lowerBodyGeo, bodyMaterial);
    lowerBody.position.y = 0.6;
    lowerBody.castShadow = true;
    lowerBody.receiveShadow = true;
    this.mesh.add(lowerBody);

    // 上层装甲板（斜面）
    const upperBodyGeo = new THREE.BoxGeometry(1.8, 0.4, 2.6);
    const upperBody = new THREE.Mesh(upperBodyGeo, bodyMaterial);
    upperBody.position.set(0, 1.0, -0.2);
    upperBody.castShadow = true;
    upperBody.receiveShadow = true;
    this.mesh.add(upperBody);

    // 前装甲板（倾斜）
    const frontArmorGeo = new THREE.BoxGeometry(1.6, 0.5, 0.15);
    const frontArmor = new THREE.Mesh(frontArmorGeo, bodyMaterial);
    frontArmor.position.set(0, 0.75, 1.5);
    frontArmor.rotation.x = -Math.PI / 6;
    frontArmor.castShadow = true;
    this.mesh.add(frontArmor);

    // 后装甲板
    const rearArmorGeo = new THREE.BoxGeometry(1.6, 0.6, 0.15);
    const rearArmor = new THREE.Mesh(rearArmorGeo, bodyMaterial);
    rearArmor.position.set(0, 0.75, -1.6);
    rearArmor.castShadow = true;
    this.mesh.add(rearArmor);
  }

  private createTurret(color: number): void {
    this.turretGroup = new THREE.Group();
    this.turretGroup.position.y = 1.2;

    const turretMaterial = new THREE.MeshStandardMaterial({
      color: color,
      roughness: 0.5,
      metalness: 0.5
    });

    // 炮塔主体（八边形更真实）
    const turretGeo = new THREE.CylinderGeometry(0.9, 1.0, 0.7, 8);
    const turretMesh = new THREE.Mesh(turretGeo, turretMaterial);
    turretMesh.castShadow = true;
    this.turretGroup.add(turretMesh);

    // 炮塔顶部装甲
    const turretTopGeo = new THREE.CylinderGeometry(0.85, 0.9, 0.15, 8);
    const turretTop = new THREE.Mesh(turretTopGeo, turretMaterial);
    turretTop.position.y = 0.425;
    turretTop.castShadow = true;
    this.turretGroup.add(turretTop);

    // 舱盖
    const hatchGeo = new THREE.CylinderGeometry(0.35, 0.35, 0.1, 8);
    const hatchMaterial = new THREE.MeshStandardMaterial({
      color: color === 0x00ff00 ? 0x004400 : 0x440000,
      roughness: 0.7,
      metalness: 0.6
    });
    const hatch = new THREE.Mesh(hatchGeo, hatchMaterial);
    hatch.position.set(-0.3, 0.55, 0);
    hatch.castShadow = true;
    this.turretGroup.add(hatch);

    // 炮管基座
    const mantletGeo = new THREE.BoxGeometry(0.6, 0.5, 0.4);
    const mantlet = new THREE.Mesh(mantletGeo, turretMaterial);
    mantlet.position.set(0, 0, 0.7);
    mantlet.castShadow = true;
    this.turretGroup.add(mantlet);

    // 炮管主体
    const barrelMaterial = new THREE.MeshStandardMaterial({
      color: 0x2a2a2a,
      roughness: 0.4,
      metalness: 0.7
    });

    const barrelGeo = new THREE.CylinderGeometry(0.12, 0.14, 2.2, 12);
    this.barrelMesh = new THREE.Mesh(barrelGeo, barrelMaterial);
    this.barrelMesh.position.set(0, 0, 1.5);
    this.barrelMesh.rotation.x = Math.PI / 2;
    this.barrelMesh.castShadow = true;
    this.turretGroup.add(this.barrelMesh);

    // 炮口制退器
    const muzzleBrakeGeo = new THREE.CylinderGeometry(0.18, 0.16, 0.3, 8);
    const muzzleBrake = new THREE.Mesh(muzzleBrakeGeo, barrelMaterial);
    muzzleBrake.position.set(0, 0, 2.5);
    muzzleBrake.rotation.x = Math.PI / 2;
    muzzleBrake.castShadow = true;
    this.turretGroup.add(muzzleBrake);

    // 炮口
    const muzzleGeo = new THREE.CylinderGeometry(0.11, 0.11, 0.15, 12);
    const muzzleMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a1a1a,
      roughness: 0.8
    });
    const muzzle = new THREE.Mesh(muzzleGeo, muzzleMaterial);
    muzzle.position.set(0, 0, 2.7);
    muzzle.rotation.x = Math.PI / 2;
    this.turretGroup.add(muzzle);

    this.mesh.add(this.turretGroup);
  }

  private createBodyDetails(color: number): void {
    const detailMaterial = new THREE.MeshStandardMaterial({
      color: color === 0x00ff00 ? 0x003300 : 0x330000,
      roughness: 0.7,
      metalness: 0.5
    });

    // 前灯（左）
    const lightGeo = new THREE.CylinderGeometry(0.12, 0.12, 0.1, 8);
    const lightMaterial = new THREE.MeshStandardMaterial({
      color: 0xffff88,
      emissive: 0xffff44,
      emissiveIntensity: 0.3,
      roughness: 0.2,
      metalness: 0.8
    });

    const leftLight = new THREE.Mesh(lightGeo, lightMaterial);
    leftLight.position.set(-0.6, 0.9, 1.6);
    leftLight.rotation.x = Math.PI / 2;
    leftLight.castShadow = true;
    this.mesh.add(leftLight);

    // 前灯（右）
    const rightLight = leftLight.clone();
    rightLight.position.set(0.6, 0.9, 1.6);
    this.mesh.add(rightLight);

    // 工具箱（左侧）
    const toolBoxGeo = new THREE.BoxGeometry(0.15, 0.3, 0.8);
    const toolBox = new THREE.Mesh(toolBoxGeo, detailMaterial);
    toolBox.position.set(-1.15, 0.7, 0.5);
    toolBox.castShadow = true;
    this.mesh.add(toolBox);

    // 工具箱（右侧）
    const toolBoxRight = toolBox.clone();
    toolBoxRight.position.set(1.15, 0.7, 0.5);
    this.mesh.add(toolBoxRight);

    // 排气管（左后方）
    const exhaustGeo = new THREE.CylinderGeometry(0.1, 0.12, 0.4, 8);
    const exhaustMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a1a1a,
      roughness: 0.9,
      metalness: 0.7
    });
    const exhaustLeft = new THREE.Mesh(exhaustGeo, exhaustMaterial);
    exhaustLeft.position.set(-0.8, 1.0, -1.5);
    exhaustLeft.castShadow = true;
    this.mesh.add(exhaustLeft);

    // 排气管（右后方）
    const exhaustRight = exhaustLeft.clone();
    exhaustRight.position.set(0.8, 1.0, -1.5);
    this.mesh.add(exhaustRight);

    // 侧裙板（左）
    const sideArmorGeo = new THREE.BoxGeometry(0.08, 0.4, 2.8);
    const sideArmor = new THREE.Mesh(sideArmorGeo, detailMaterial);
    sideArmor.position.set(-1.15, 0.4, 0);
    sideArmor.castShadow = true;
    this.mesh.add(sideArmor);

    // 侧裙板（右）
    const sideArmorRight = sideArmor.clone();
    sideArmorRight.position.set(1.15, 0.4, 0);
    this.mesh.add(sideArmorRight);

    // 天线
    const antennaGeo = new THREE.CylinderGeometry(0.02, 0.02, 1.2, 6);
    const antennaMaterial = new THREE.MeshStandardMaterial({
      color: 0x333333,
      roughness: 0.5,
      metalness: 0.9
    });
    const antenna = new THREE.Mesh(antennaGeo, antennaMaterial);
    antenna.position.set(0.5, 1.8, -0.5);
    antenna.rotation.z = Math.PI / 12;
    antenna.castShadow = true;
    this.mesh.add(antenna);
  }

  move(forward: number, turn: number, deltaTime: number): void {
    if (!this.isAlive) return;

    // 转向
    const turnAmount = turn * this.turnSpeed * deltaTime;
    this.currentRotation += turnAmount;

    // 使用 setFromAxisAngle 设置 Y 轴旋转
    const axis = new CANNON.Vec3(0, 1, 0);
    this.body.quaternion.setFromAxisAngle(axis, this.currentRotation);

    // 前进/后退
    if (forward !== 0) {
      const forceX = Math.sin(this.currentRotation) * forward * this.moveSpeed;
      const forceZ = Math.cos(this.currentRotation) * forward * this.moveSpeed;

      this.body.velocity.x = forceX;
      this.body.velocity.z = forceZ;
    } else {
      this.body.velocity.x *= 0.9;
      this.body.velocity.z *= 0.9;
    }
  }

  rotateTurret(angle: number): void {
    this.turretRotation = angle;
    this.turretGroup.rotation.y = angle;
  }

  getRotation(): number {
    return this.currentRotation;
  }

  getBarrelEnd(): THREE.Vector3 {
    const barrelEnd = new THREE.Vector3(0, 0, 2);
    barrelEnd.applyMatrix4(this.turretGroup.matrixWorld);
    return barrelEnd;
  }

  getBarrelDirection(): THREE.Vector3 {
    const direction = new THREE.Vector3(0, 0, 1);
    direction.applyQuaternion(this.turretGroup.getWorldQuaternion(new THREE.Quaternion()));
    return direction.normalize();
  }

  update(deltaTime: number): void {
    // 同步网格位置到物理体
    this.mesh.position.copy(this.body.position as any);
    this.mesh.quaternion.copy(this.body.quaternion as any);

    // 更新射击冷却
    if (!this.canShoot) {
      this.cooldownTimer += deltaTime;
      if (this.cooldownTimer >= this.shootCooldown) {
        this.canShoot = true;
        this.cooldownTimer = 0;
      }
    }

    // 更新护盾时间
    if (this.hasShield) {
      this.shieldTime -= deltaTime;
      if (this.shieldTime <= 0) {
        this.hasShield = false;
        this.shieldTime = 0;
      }
    }
  }

  shoot(): void {
    if (!this.canShoot || !this.isAlive) return;
    this.canShoot = false;
  }

  takeDamage(damage: number): void {
    // 护盾减免伤害
    if (this.hasShield) {
      damage *= 0.5; // 护盾减少 50% 伤害
    }

    this.health -= damage;
    if (this.health <= 0) {
      this.health = 0;
      this.isAlive = false;
    }
  }

  activateShield(duration: number): void {
    this.hasShield = true;
    this.shieldTime = Math.max(this.shieldTime, duration);
  }

  heal(amount: number): void {
    this.health = Math.min(this.health + amount, this.maxHealth);
  }

  reset(x: number, z: number): void {
    this.health = this.maxHealth;
    this.isAlive = true;
    this.canShoot = true;
    this.cooldownTimer = 0;
    this.currentRotation = 0;
    this.hasShield = false;
    this.shieldTime = 0;

    this.body.position.set(x, 0.5, z);
    this.body.velocity.set(0, 0, 0);
    this.body.angularVelocity.set(0, 0, 0);
    this.body.quaternion.set(0, 0, 0, 1);

    this.turretRotation = 0;
    this.turretGroup.rotation.y = 0;
  }
}
